// SimplifiedDashboard.tsx
import {
    ArrowLeftStartOnRectangleIcon,
    ArrowPathIcon,
    Cog6ToothIcon,
    HomeIcon,
    MegaphoneIcon,
    RectangleGroupIcon,
    StarIcon,
    TicketIcon,
    UserIcon
} from '@heroicons/react/20/solid';
import { useEffect } from 'react';
import { Link, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import {
    Divider,
    Sidebar,
    SidebarBody,
    SidebarFooter,
    SidebarHeader,
    SidebarItem,
    SidebarLabel,
    SidebarLayout,
    SidebarSection,
} from '../../components/catalyst';
import { DashboardLayout } from '../../components/DashboardLayout';
import { DiscordIcon, InstagramIcon, RedditIcon } from '../../components/footer/icons';
import NotFound from '../../components/notFound';
import config from '../../config';
import Logout from '../../dashboard/logout';
import PremiumOptions from '../../dashboard/premium/premiumOptions';
import Profile from '../../dashboard/profile';
import SyncMain from '../../dashboard/sync/syncMain';
import ViewSync from '../../dashboard/sync/viewSync';
import ConnectedAccounts from '../../dashboard/transfer/connectedAcccounts';
import EditTransfer from '../../dashboard/transfer/history/editTransfer';
import TransferHistory from '../../dashboard/transfer/history/transferHistory';
import CompleteManualTransfer from '../../dashboard/transfer/manual/completeManualTransfer';
import { Backup } from './Backup/backup';

export function Dashboard() {
    const location = useLocation();
    const navigate = useNavigate();

    useEffect(() => {
        const user = localStorage.getItem("user");
        const loggedInAt = localStorage.getItem("logggedInAt");

        if (!user || !loggedInAt) return navigate(config.loginPath);

        const ageInHours = (new Date().getTime() - new Date(loggedInAt).getTime()) / 1000 / 60 / 60;
        if (ageInHours > 22) {
            localStorage.removeItem("user");
            localStorage.removeItem("logggedInAt");
            navigate(config.loginPath);
        }
    }, [navigate]);

    // List of “Account” links (first section)
    const accountLinks = [
        {
            label: 'Connect Accounts',
            href: '/dashboard/accounts',
            icon: RectangleGroupIcon,
            key: '/dashboard/accounts',
        },
        {
            label: 'Transfer History',
            href: '/dashboard/transferHistory',
            icon: TicketIcon,
            key: '/dashboard/transferHistory',
        },
        { label: 'Sync', href: '/dashboard/sync', icon: ArrowPathIcon, key: '/dashboard/sync' },
        { label: 'Premium', href: '/dashboard/premium', icon: StarIcon, key: '/dashboard/premium' },
        { label: 'Profile', href: '/dashboard/profile', icon: UserIcon, key: '/dashboard/profile' },
    ];


    // List of "Social" (second) links
    const socialLinks = [
        { label: 'Discord', href: 'https://discord.gg/6T35Rj74', icon: DiscordIcon, key: '/discord' },
        { label: 'Reddit', href: 'https://www.reddit.com/r/MusicPorter/', icon: RedditIcon, key: '/reddit' },
        { label: 'Instagram', href: 'https://www.instagram.com/musicporter_app/', icon: InstagramIcon, key: '/instagram' },
    ];

    // List of “General” (third) links
    const generalLinks = [
        { label: 'Home', href: '/', icon: HomeIcon, key: '/' },
        { label: 'Blog', href: '/blog', icon: MegaphoneIcon, key: '/blog' },
        { label: 'Contact', href: '/contact-us', icon: Cog6ToothIcon, key: '/contact-us' },
    ];

    // Helper to render each <SidebarItem> with “current” prop
    const renderLinks = (
        items: { label: string; href: string; icon: React.FC; key: string }[]
    ) =>
        items.map(({ label, href, icon: Icon, key }) => {
            // Check if it's an external link (starts with http)
            const isExternal = href.startsWith('http');
            
            return (
                <Link to={href} target={isExternal ? "_blank" : undefined} rel={isExternal ? "noopener noreferrer" : undefined}>
                    <SidebarItem
                        key={key}
                        current={isExternal ? false : location.pathname === href}
                    >
                        <Icon />
                        <SidebarLabel>{label}</SidebarLabel>
                    </SidebarItem>
                </Link>
            );
        });

    return (
        <SidebarLayout
            navbar={<div className="h-4" />} // Spacer
            sidebar={
                <Sidebar>
                    <SidebarHeader>
                        <div className="flex items-center justify-between w-full px-3">
                            {/* Logo */}
                            <Link to="/" className="flex items-center">
                                <img
                                    src={require('../../assets/logo.png')}
                                    className="h-6 mr-2 sm:h-9"
                                    alt="Music Porter"
                                />
                            </Link>

                            {/* Theme Toggle */}
                            {/* <ThemeToggle /> */}
                        </div>
                    </SidebarHeader>
                    <SidebarBody>
                        <div className="px-4 py-2">
                            <span className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">
                                Account Actions
                            </span>
                        </div>

                        {/* Section 1: Account Actions */}
                        <SidebarSection>{renderLinks(accountLinks)}</SidebarSection>

                        {/* Divider */}
                        <Divider className="my-3"/>
                        <div className="px-4 py-2">
                            <span className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">
                                Reach us on
                            </span>
                        </div>
                        {/* Section 2: Social links (hidden conditionally via CSS) */}
                        <SidebarSection className="hide-general-menu-sidebar">
                            {renderLinks(socialLinks)}
                        </SidebarSection>

                        {/* Divider */}
                        <Divider className="my-3"/>


                        <div className="px-4 py-2">
                            <span className="text-xs text-zinc-500 uppercase tracking-wider font-semibold">
                                Read more
                            </span>
                        </div>
                        {/* Section 3: General Nav (hidden conditionally via CSS) */}
                        <SidebarSection className="hide-general-menu-sidebar">
                            {renderLinks(generalLinks)}
                        </SidebarSection>

                        {/* Divider */}
                        <Divider className="my-3"/>

                        {/* Section 4: Logout */}
                        <SidebarSection>
                            <SidebarItem
                                href="/dashboard/logout"
                                current={location.pathname === '/dashboard/logout'}
                            >
                                <ArrowLeftStartOnRectangleIcon />
                                <SidebarLabel>Logout</SidebarLabel>
                            </SidebarItem>
                        </SidebarSection>
                    </SidebarBody>
                    <SidebarFooter className="max-lg:hidden" />
                </Sidebar>
            }
        >
            <DashboardLayout>
                <Routes>
                    <Route path='*' element={<NotFound isDashboard />} />
                    <Route path='/accounts' element={<ConnectedAccounts />} />
                    <Route path='/transferHistory' element={<TransferHistory />} />
                    <Route path='/transferHistory/:transferId/edit' element={<EditTransfer />} />
                    <Route path='/transferHistory/:transferId/complete' element={<CompleteManualTransfer />} />
                    <Route path='/sync' element={<SyncMain />} />
                    <Route path='/sync/:syncId' element={<ViewSync />} />
                    <Route path='/profile' element={<Profile />} />
                    <Route path='/backups' element={<Backup />} />
                    <Route path='/premium' element={<PremiumOptions />} />
                    <Route path='/logout' element={<Logout />} />
                </Routes>
            </DashboardLayout>
        </SidebarLayout>
    );
}
